import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { CompanyInfo, QuoteData } from '../types';
import {
  formatNumber,
  formatDateKorean,
  formatDateReceipt,
  formatMonthDay,
  formatAmountKorean,
  sanitizeFilename,
  formatDateCompact,
} from './formatters';

// ============================================================
// 공통 스타일 — 시스템 한글 폰트 우선 사용 (폰트 로딩 지연 없음)
// ============================================================
const COMMON_STYLE = `
  * { margin:0; padding:0; box-sizing:border-box; }
  body {
    font-family: 'Malgun Gothic', '맑은 고딕', 'Apple SD Gothic Neo',
                 'Noto Sans KR', 'NanumGothic', 'Dotum', '돋움', sans-serif;
    background: #fff;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  table { border-collapse: collapse; width: 100%; }
  th, td { vertical-align: middle; }
`;

// ============================================================
// 견적서 HTML — A4 가로 (297 × 210 mm)
// ============================================================
export function buildQuoteHTML(data: QuoteData, company: CompanyInfo): string {
  const items   = data.items.filter(i => i.name.trim() !== '');
  const totalSupply = items.reduce((s, i) => s + i.supplyAmount, 0);
  const totalTax    = data.taxInvoice === 'yes' ? items.reduce((s, i) => s + i.taxAmount, 0) : 0;
  const totalAmount = totalSupply + totalTax;

  const bankLine =
    data.taxInvoice === 'yes'
      ? '신한 &nbsp; 주식회사속기법인지원 &nbsp; 140-011-879543'
      : '신한 &nbsp; 방지원 &nbsp; 110-364-065830';
  const taxLabel = data.taxInvoice === 'yes' ? '(부가세포함)' : '(부가세없음)';

  const emptyCount = Math.max(0, 7 - items.length);

  const itemRows = items.map((item, idx) => `
    <tr style="height:21px;">
      <td style="text-align:center;border:1px solid #444;padding:3px 4px;">${idx + 1}</td>
      <td style="padding:3px 6px;border:1px solid #444;">${item.name}</td>
      <td style="text-align:center;border:1px solid #444;padding:3px 4px;">${item.spec || ''}</td>
      <td style="text-align:center;border:1px solid #444;padding:3px 4px;">${item.qty}</td>
      <td style="text-align:right;border:1px solid #444;padding:3px 7px;">${formatNumber(item.unitPrice)}</td>
      <td style="text-align:right;border:1px solid #444;padding:3px 7px;">${formatNumber(item.supplyAmount)}</td>
      <td style="text-align:right;border:1px solid #444;padding:3px 7px;">${data.taxInvoice === 'yes' ? formatNumber(item.taxAmount) : ''}</td>
      <td style="padding:3px 5px;border:1px solid #444;">${item.note || ''}</td>
    </tr>`).join('');

  const emptyRows = Array(emptyCount).fill(
    `<tr style="height:21px;">
      <td style="border:1px solid #444;"></td><td style="border:1px solid #444;"></td>
      <td style="border:1px solid #444;"></td><td style="border:1px solid #444;"></td>
      <td style="border:1px solid #444;"></td><td style="border:1px solid #444;"></td>
      <td style="border:1px solid #444;"></td><td style="border:1px solid #444;"></td>
    </tr>`).join('');

  /* 도장: 견적서에도 대표자 이름 옆에 표시 */
  const sealHtml = company.sealImage
    ? `<img src="${company.sealImage}"
           style="display:inline-block;vertical-align:middle;
                  width:${company.sealWidth || 40}px;
                  margin-left:4px;opacity:0.92;" />`
    : '';

  return `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<style>
${COMMON_STYLE}
body { width:277mm; padding:8mm 12mm 6mm; font-size:9.5pt; }
h1 {
  text-align:center; font-size:22pt; font-weight:900;
  letter-spacing:20px; margin-bottom:7px;
}
.top-row {
  display:flex; justify-content:space-between;
  margin-bottom:5px; font-size:9pt;
}
.main-row { display:flex; gap:8px; margin-bottom:5px; }

/* 의뢰인 박스 */
.client-box {
  border:2px solid #000; padding:8px 12px;
  flex:1.3; display:flex; align-items:center; min-height:68px;
}
.client-inner { line-height:2; }
.client-name { font-size:12.5pt; font-weight:700; }
.client-kwa  { font-size:14pt;   font-weight:900; margin-top:2px; }

/* 공급자 박스 */
.co-box  { flex:1; border:2px solid #000; }
.co-box table { width:100%; }
.co-box td { border:1px solid #666; padding:2.5px 6px; font-size:8.5pt; }
.co-hd  { background:#e0e0e0; text-align:center; font-weight:700;
          font-size:9.5pt; padding:4px !important; }
.co-th  { background:#f0f0f0; font-weight:700; width:58px;
          white-space:nowrap; text-align:center; }

.meta   { font-size:9pt; margin-bottom:4px; line-height:1.8; }
.greet  { font-size:9.5pt; margin-bottom:5px; }

.total-box {
  border:2.5px solid #000; padding:6px 12px;
  margin-bottom:7px; font-size:11pt; font-weight:700; line-height:1.5;
}
.total-box .tax-lbl { color:#c00000; }

/* 품목 테이블 */
.it { margin-bottom:6px; font-size:8.5pt; }
.it th {
  background:#f0f0f0; text-align:center;
  padding:4px 3px; border:1px solid #444; font-weight:700;
}
.total-r td {
  background:#f5f5f5; font-weight:700;
  padding:4px 7px; border:1px solid #444;
}

/* 하단 납기 테이블 */
.ft { margin-bottom:5px; font-size:8.5pt; }
.ft th {
  background:#f0f0f0; text-align:center; font-weight:700;
  padding:4px 6px; border:1px solid #444; min-width:62px;
}
.ft td { padding:4px 7px; border:1px solid #444; }

.note-area { font-size:8.5pt; line-height:2; margin-top:3px; }
.note-title { font-weight:700; font-size:9pt; }
</style>
</head>
<body>
<h1>견 &nbsp; 적 &nbsp; 서</h1>

<div class="top-row">
  <div><b>NO :</b>&nbsp;${data.quoteNo}</div>
  <div><b>등록번호</b>&nbsp;&nbsp;${company.bizNumber}</div>
</div>

<div class="main-row">
  <div class="client-box">
    <div class="client-inner">
      <div class="client-name">${data.clientName || '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'}</div>
      <div class="client-kwa">귀&nbsp;&nbsp;하</div>
    </div>
  </div>
  <div class="co-box">
    <table>
      <tr><td class="co-hd" colspan="2">공 &nbsp; 급 &nbsp; 자</td></tr>
      <tr>
        <td class="co-th">상&nbsp;&nbsp;&nbsp;호</td>
        <td>${company.name}</td>
      </tr>
      <tr>
        <td class="co-th">대&nbsp;표&nbsp;자</td>
        <td>
          ${company.representative}
          ${sealHtml}
        </td>
      </tr>
      <tr><td class="co-th">업&nbsp;&nbsp;&nbsp;태</td><td>${company.bizType}</td></tr>
      <tr><td class="co-th">종&nbsp;&nbsp;&nbsp;목</td><td>${company.bizItem}</td></tr>
      <tr><td class="co-th">주&nbsp;&nbsp;&nbsp;소</td><td>${company.address}</td></tr>
    </table>
  </div>
</div>

<div class="meta">
  견적일자&nbsp;:&nbsp;<b>${formatDateKorean(data.issueDate)}</b>
  &nbsp;&nbsp;&nbsp;&nbsp;
  전화번호&nbsp;${company.phone}
  &nbsp;&nbsp;&nbsp;&nbsp;
  팩스번호&nbsp;${company.fax}
</div>
<div class="greet">견적요청에 감사드리며 아래와 같이 견적합니다.</div>

<div class="total-box">
  합계금액&nbsp;:&nbsp;${formatAmountKorean(totalAmount)}
  &nbsp;&nbsp;(${formatNumber(totalAmount)})
  &nbsp;&nbsp;<span class="tax-lbl">${taxLabel}</span>
</div>

<table class="it">
  <thead>
    <tr>
      <th style="width:36px;">순번</th>
      <th>품목명</th>
      <th style="width:66px;">규격</th>
      <th style="width:46px;">수량</th>
      <th style="width:88px;">단가</th>
      <th style="width:92px;">공급가액</th>
      <th style="width:82px;">세액</th>
      <th>비고</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows}
    ${emptyRows}
    <tr class="total-r">
      <td colspan="5" style="text-align:center;">합 &nbsp; 계</td>
      <td style="text-align:right;">${formatNumber(totalSupply)}</td>
      <td style="text-align:right;">${data.taxInvoice === 'yes' ? formatNumber(totalTax) : ''}</td>
      <td></td>
    </tr>
  </tbody>
</table>

<table class="ft">
  <tr>
    <th>납기일자</th><td>${data.deliveryDate || '&nbsp;'}</td>
    <th>납품장소</th><td>${data.deliveryPlace || '&nbsp;'}</td>
    <th>유효일자</th><td>${data.validDate || '&nbsp;'}</td>
    <th>결제조건</th><td>${data.paymentTerms || '&nbsp;'}</td>
  </tr>
</table>

<div class="note-area">
  <div class="note-title">※ 비고</div>
  <div>&#9658; 입금계좌&nbsp;:&nbsp;${bankLine}</div>
  ${data.note ? `<div>&#9658; ${data.note}</div>` : ''}
  <div style="color:#666;font-size:8pt;">
    ${data.taxInvoice === 'yes'
      ? '* 세금계산서 발행 건입니다. 입금 확인 후 발행해 드립니다.'
      : '* 세금계산서 미발행 건입니다. (부가세 면제)'}
  </div>
</div>
</body>
</html>`;
}

// ============================================================
// 영수증 HTML — A4 세로 (210 × 297 mm)
// ============================================================
export function buildReceiptHTML(data: QuoteData, company: CompanyInfo): string {
  const items = data.items.filter(i => i.name.trim() !== '');
  const totalAmount = items.reduce((s, i) => s + i.supplyAmount, 0);
  const emptyCount  = Math.max(0, 6 - items.length);

  const itemRows = items.map(item => `
    <tr style="height:22px;">
      <td style="text-align:center;border:1px solid #444;padding:3px 4px;">
        ${formatMonthDay(data.issueDate)}
      </td>
      <td style="padding:3px 7px;border:1px solid #444;">${item.name}</td>
      <td style="text-align:center;border:1px solid #444;padding:3px 4px;">${item.qty}</td>
      <td style="text-align:right;border:1px solid #444;padding:3px 7px;">
        &#65510;&nbsp;${formatNumber(item.unitPrice)}
      </td>
      <td style="text-align:right;border:1px solid #444;padding:3px 7px;">
        &#65510;&nbsp;${formatNumber(item.supplyAmount)}
      </td>
    </tr>`).join('');

  const emptyRows = Array(emptyCount).fill(
    `<tr style="height:22px;">
      <td style="border:1px solid #444;"></td><td style="border:1px solid #444;"></td>
      <td style="border:1px solid #444;"></td><td style="border:1px solid #444;"></td>
      <td style="border:1px solid #444;"></td>
    </tr>`).join('');

  /* 도장: 방지원 이름 오른쪽에 이미지로만 표시 */
  const sealHtml = company.sealImage
    ? `<img src="${company.sealImage}"
           style="display:inline-block;vertical-align:middle;
                  width:${company.sealWidth || 48}px;
                  margin-left:6px;opacity:0.92;" />`
    : '';

  return `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<style>
${COMMON_STYLE}
body { width:182mm; padding:12mm 14mm 10mm; font-size:9.5pt; }

.r-header { text-align:center; position:relative; margin-bottom:12px; }
h1 { font-size:22pt; font-weight:900; letter-spacing:20px; }
.supply-lbl {
  position:absolute; right:0; top:7px;
  border:1.5px solid #000; padding:3px 7px;
  font-size:9pt; font-weight:700;
}

/* No. 줄 — 영문 그대로 */
.doc-no  { font-size:9.5pt; margin-bottom:4px; }
/* 의뢰인 2줄 표시 */
.client-name-line { font-size:13pt; font-weight:700; margin-bottom:2px; }
.client-kwa-line  { font-size:13pt; font-weight:900; margin-bottom:10px; }

/* 공급자 테이블 */
.co-t { margin-bottom:9px; font-size:9pt; }
.co-t td { border:1px solid #555; padding:4px 8px; }
.co-h  { background:#f0f0f0; font-weight:700; width:126px; white-space:nowrap; }
.co-hd { background:#e0e0e0; text-align:center; font-weight:700;
         font-size:9.5pt; padding:5px; }

/* 합계 요약 테이블 */
.sum-t { margin-bottom:9px; font-size:9pt; }
.sum-t th { background:#f0f0f0; text-align:center; font-weight:700;
            padding:5px 8px; border:1px solid #555; }
.sum-t td { padding:5px 8px; border:1px solid #555; }

/* 영수 문구 */
.phrase {
  font-size:11.5pt; font-weight:700; text-align:center;
  border:1.5px solid #000; padding:7px; margin-bottom:9px;
}

/* 품목 테이블 */
.it { font-size:9pt; margin-bottom:7px; }
.it th { background:#f0f0f0; text-align:center;
         font-weight:700; padding:4px 6px; border:1px solid #555; }
.total-r td {
  background:#f5f5f5; font-weight:700;
  padding:4px 7px; border:1px solid #555;
}

.footer-note {
  font-size:8pt; text-align:center; color:#555;
  margin-top:7px; border-top:1px dashed #aaa; padding-top:6px;
}
</style>
</head>
<body>

<div class="r-header">
  <h1>영 &nbsp; 수 &nbsp; 증</h1>
  <span class="supply-lbl">(공급받는자용)</span>
</div>

<!-- No. 는 번역하지 않음 — 영수증 번호 표기 -->
<div class="doc-no"><span style="font-family:Arial,sans-serif;font-weight:700;">No.</span>&nbsp;&nbsp;<b>${data.receiptNo}</b></div>

<!-- 의뢰인 2줄 표시 -->
<div class="client-name-line">${data.clientName || '　　　　　　　'}</div>
<div class="client-kwa-line">귀&nbsp;&nbsp;하</div>

<table class="co-t">
  <tr><td class="co-hd" colspan="2">공 &nbsp; 급 &nbsp; 자</td></tr>
  <tr><td class="co-h">사업자등록번호</td><td>${company.bizNumber}</td></tr>
  <tr>
    <td class="co-h">상 &nbsp;&nbsp;&nbsp; 호</td>
    <td>
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <span>주식회사 속기법인지원</span>
        <span style="display:flex;align-items:center;gap:0px;">
          성명&nbsp;&nbsp;${company.representative}${sealHtml}
        </span>
      </div>
    </td>
  </tr>
  <tr><td class="co-h">사업장 소재지</td><td>${company.receiptAddress}</td></tr>
  <tr><td class="co-h">업 &nbsp;&nbsp;&nbsp; 태</td><td>${company.receiptBizType}</td></tr>
  <tr><td class="co-h">종 &nbsp;&nbsp;&nbsp; 목</td><td>${company.bizItem}</td></tr>
</table>

<table class="sum-t">
  <thead>
    <tr>
      <th style="width:88px;">일 자</th>
      <th>공급대가총액</th>
      <th>비 고</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td style="text-align:center;font-weight:700;">${formatDateReceipt(data.issueDate)}</td>
      <td style="text-align:right;font-weight:700;font-size:10.5pt;">
        &#65510;&nbsp;${formatNumber(totalAmount)}
      </td>
      <td>${data.note || '&nbsp;'}</td>
    </tr>
  </tbody>
</table>

<div class="phrase">위 금액을 정히 영수(청구)함</div>

<table class="it">
  <thead>
    <tr>
      <th style="width:58px;">월 일</th>
      <th>품 목</th>
      <th style="width:48px;">수량</th>
      <th style="width:108px;">단 가</th>
      <th style="width:118px;">공급대가(금액)</th>
    </tr>
  </thead>
  <tbody>
    ${itemRows}
    ${emptyRows}
    <tr class="total-r">
      <td style="text-align:center;" colspan="3">합 &nbsp;&nbsp;&nbsp; 계</td>
      <td style="border:1px solid #555;"></td>
      <td style="text-align:right;">&#65510;&nbsp;${formatNumber(totalAmount)}</td>
    </tr>
  </tbody>
</table>

<div class="footer-note">
  부가가치세법시행규칙 제25조의 규정에 의한(영수증)으로 개정
</div>

</body>
</html>`;
}

// ============================================================
// HTML → PDF 변환
// scale:1.8 + JPEG quality:0.82 → 용량 대폭 절감 (scale:3 PNG 대비 ~80% 감소)
// 텍스트·선은 충분히 선명, 이메일 첨부에 적합한 수준 유지
// ============================================================
async function htmlToPdf(
  html: string,
  orientation: 'landscape' | 'portrait',
  filename: string
): Promise<void> {
  return new Promise(async (resolve, reject) => {
    /* 오프스크린 iframe */
    const iframe = document.createElement('iframe');
    iframe.style.cssText = [
      'position:fixed',
      'left:-9999px',
      'top:0',
      orientation === 'landscape' ? 'width:1240px;height:880px' : 'width:900px;height:1270px',
      'z-index:-999',
      'border:none',
      'visibility:hidden',
    ].join(';');
    document.body.appendChild(iframe);

    const iDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!iDoc) {
      document.body.removeChild(iframe);
      reject(new Error('iframe unavailable'));
      return;
    }

    iDoc.open();
    iDoc.write(html);
    iDoc.close();

    /* 렌더링 + 이미지 대기 */
    await new Promise(r => setTimeout(r, 600));

    try {
      const el = iDoc.body as HTMLElement;

      /* 실제 콘텐츠 크기 캡처 */
      const captureW = el.scrollWidth  || (orientation === 'landscape' ? 1200 : 860);
      const captureH = el.scrollHeight || (orientation === 'landscape' ? 840 : 1200);

      /* scale:1.8 — 이메일 첨부 최적화 (선명도 유지 + 용량 절감) */
      const SCALE = 1.8;
      const canvas = await html2canvas(el, {
        scale        : SCALE,
        useCORS      : true,
        allowTaint   : true,
        logging      : false,
        backgroundColor: '#ffffff',
        imageTimeout : 20000,
        width        : captureW,
        height       : captureH,
        windowWidth  : captureW,
        windowHeight : captureH,
      });

      /* JPEG quality:0.82 — 텍스트 문서에 충분한 품질, 용량 대폭 절감 */
      const imgData = canvas.toDataURL('image/jpeg', 0.82);

      const pdfW = orientation === 'landscape' ? 297 : 210;
      const pdfH = orientation === 'landscape' ? 210 : 297;

      const pdf = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

      const imgW   = canvas.width;
      const imgH   = canvas.height;
      const ratio  = imgH / imgW;

      let rW = pdfW - 0;
      let rH = rW * ratio;

      if (rH > pdfH) {
        rH = pdfH;
        rW = rH / ratio;
      }

      const mx = (pdfW - rW) / 2;
      const my = (pdfH - rH) / 2;

      pdf.addImage(imgData, 'JPEG', mx, my, rW, rH);
      pdf.save(filename);
      resolve();
    } catch (e) {
      reject(e);
    } finally {
      document.body.removeChild(iframe);
    }
  });
}

// ============================================================
// 공개 함수
// ============================================================
export async function downloadQuotePDF(data: QuoteData, company: CompanyInfo): Promise<void> {
  const taxLabel   = data.taxInvoice === 'yes' ? '세금계산서발행' : '세금계산서미발행';
  const dateStr    = formatDateCompact(data.issueDate);
  const clientName = sanitizeFilename(data.clientName || '미입력');
  const filename   = `${clientName}_견적서_${taxLabel}_${dateStr}.pdf`;
  await htmlToPdf(buildQuoteHTML(data, company), 'landscape', filename);
}

export async function downloadReceiptPDF(data: QuoteData, company: CompanyInfo): Promise<void> {
  const dateStr    = formatDateCompact(data.issueDate);
  const clientName = sanitizeFilename(data.clientName || '미입력');
  const filename   = `${clientName}_영수증_${dateStr}.pdf`;
  await htmlToPdf(buildReceiptHTML(data, company), 'portrait', filename);
}

export async function downloadBothPDF(data: QuoteData, company: CompanyInfo): Promise<void> {
  await downloadQuotePDF(data, company);
  await new Promise(r => setTimeout(r, 500));
  await downloadReceiptPDF(data, company);
}
